'use client'

import { useState, useEffect } from 'react'
import classNames from 'classnames'
import styles from './OrderInquiryForm.module.scss'
import Button from '@/components/Button/Button'
import useI18n from '@/hooks/use-i18n'
import FormField, { FormFieldChangeEvent, FormFieldProps } from '@/components/FormField/FormField'
import ContactFormResults from '@/components/ContactForm/ContactFormResults/ContactFormResults'
import { isValidEmail } from '@/utils/strings/email'
import { useFormSubmissionState } from '@/hooks/useFormSubmissionState'

type OrderInquiryFormProps = {
  className?: string
}

type ValidationError = {
  field: string
  errorMessage: string
}

export type OrderInquiryFormField = 'name' | 'email' | 'orderNumber' | 'orderDate' | 'typeOfInquiry' | 'message'

const OrderInquiryForm = ({ className }: OrderInquiryFormProps) => {
  const { i18n } = useI18n()

  const typeOfInquiryOptions = [
    i18n('orderStatusTracking'),
    i18n('cancelOrUpdateOrder'),
    i18n('changeShippingAddress'),
    i18n('receivedDamagedIncorrectItem'),
    i18n('lostPackageMissingItem'),
    i18n('generalQuestion'),
  ]

  const handleChange = (fieldName: string) => (event: FormFieldChangeEvent) => {
    setValidationErrors(prevState => prevState.filter(error => error.field !== fieldName))

    const { value } = event?.target || { value: '' }
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: value,
    }))
  }

  const { loading, formSubmit, formSubmitSuccess, startSubmission, finishSubmission } = useFormSubmissionState()
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [honeypot, setHoneypot] = useState('')
  const [formLoadTime, setFormLoadTime] = useState<number | null>(null)

  // Record when the form was loaded for time-based bot detection
  useEffect(() => {
    setFormLoadTime(Date.now())
  }, [])

  const defaultErrorMessage = i18n('defaultFieldError') ?? 'Error'
  const formFields: Record<OrderInquiryFormField, FormFieldProps> = {
    name: {
      name: 'name',
      value: '',
      required: true,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      type: 'text',
      placeholder: i18n('name'),
      onChange: handleChange('name'),
      variant: 'minimal',
    },
    email: {
      name: 'email',
      value: '',
      required: true,
      errorMessage: i18n('invalidEmailAddress'),
      className: styles.field,
      type: 'email',
      placeholder: i18n('emailAddress'),
      onChange: handleChange('email'),
      variant: 'minimal',
      autoComplete: 'email',
    },
    orderNumber: {
      name: 'orderNumber',
      value: '',
      required: true,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      type: 'text',
      placeholder: i18n('orderNumber'),
      onChange: handleChange('orderNumber'),
      variant: 'minimal',
    },
    orderDate: {
      name: 'orderDate',
      value: '',
      required: true,
      errorMessage: i18n('invalidDateFormat'),
      className: styles.field,
      type: 'date',
      placeholder: i18n('orderDate'),
      onChange: handleChange('orderDate'),
      variant: 'minimal',
      max: new Date().toISOString().split('T')[0],
    },
    typeOfInquiry: {
      name: 'typeOfInquiry',
      value: '',
      required: true,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      type: 'select',
      placeholder: i18n('typeOfInquiry'),
      onChange: handleChange('typeOfInquiry'),
      ariaLabel: i18n('typeOfInquiry'),
      items: typeOfInquiryOptions,
      variant: 'minimal',
    },
    message: {
      name: 'message',
      value: '',
      required: true,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      type: 'textarea',
      placeholder: i18n('message'),
      onChange: handleChange('message'),
      variant: 'minimal',
    },
  } as const

  const initialState = Object.entries(formFields).reduce(
    (acc, [fieldName, fieldMetadata]) => ({ ...acc, [fieldName]: fieldMetadata.value }),
    {} as { -readonly [key in keyof typeof formFields]: string },
  )

  const [formData, setFormData] = useState<{ [key: string]: string }>(initialState)

  const getFormFieldProps = (fieldName: keyof typeof formFields): FormFieldProps => {
    return {
      ...formFields[fieldName],
      value: formData[fieldName],
      errorMessage: validationErrors.find(error => error.field === fieldName)?.errorMessage,
    }
  }

  const validateOrderDate = (date: string): { isValid: boolean; errorMessage?: string } => {
    if (!date?.trim()) {
      return { isValid: true }
    }

    // Check if date is in valid format and is a real date
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) {
      return { isValid: false, errorMessage: i18n('invalidDateFormat') }
    }

    const currentDate = new Date()
    currentDate.setHours(23, 59, 59, 999) // Set to end of today

    // Check if date is not in the future
    if (dateObj > currentDate) {
      return { isValid: false, errorMessage: i18n('orderDateCannotBeInFuture') }
    }

    return { isValid: true }
  }

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Bot detection: Check if honeypot field is filled
    if (honeypot && honeypot.trim() !== '') {
      // Silently reject - don't give feedback to bots
      return
    }

    // Bot detection: Check if form was submitted too quickly (less than 3 seconds)
    if (formLoadTime) {
      const timeSinceLoad = Date.now() - formLoadTime
      if (timeSinceLoad < 3000) {
        // Silently reject - don't give feedback to bots
        return
      }
    }

    const currentErrors: ValidationError[] = Object.values(formFields)
      .map(field => {
        const value = formData[field.name]?.trim()

        if (field.required && !value) {
          return { field: field.name, errorMessage: field.errorMessage || defaultErrorMessage }
        }

        if (field.name === 'email' && value && !isValidEmail(value)) {
          return { field: field.name, errorMessage: field.errorMessage || defaultErrorMessage }
        }

        if (field.name === 'orderDate' && value) {
          const dateValidation = validateOrderDate(value)
          if (!dateValidation.isValid) {
            return { field: field.name, errorMessage: dateValidation.errorMessage || defaultErrorMessage }
          }
        }

        return null
      })
      .filter(Boolean) as ValidationError[]

    setValidationErrors(currentErrors)

    if (currentErrors.length > 0) {
      finishSubmission(false)
      return
    }

    startSubmission()

    try {
      // Create abort controller for timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout

      const response = await fetch('/api/order-inquiry-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          honeypot,
          formLoadTime: formLoadTime?.toString(),
        }),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        // Handle 400 (validation), 500 (server error), 503 (service unavailable)
        const errorData = await response.json().catch(() => ({ success: false }))
        console.error('Order inquiry submission failed:', errorData?.message || response.statusText)
        finishSubmission(false)
        return
      }

      const data = await response.json()
      finishSubmission(data?.success === true)
      if (data?.success) {
        setFormData(initialState)
      }
    } catch (error) {
      finishSubmission(false)
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('Request timeout: The server took too long to respond')
      } else {
        console.error({ error })
      }
    }
  }

  return (
    <div className={classNames(className, styles.OrderInquiryForm)}>
      <form
        onSubmit={e => onSubmit(e)}
        noValidate
        autoComplete="on"
        name="OrderInquiryForm"
      >
        <div className={styles.formFields}>
          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('name')} />
          </div>

          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('email')} />
          </div>

          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('orderNumber')} />
          </div>

          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('orderDate')} />
          </div>

          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('typeOfInquiry')} />
          </div>

          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('message')} />
          </div>

          {/* Honeypot field - hidden from users but visible to bots */}
          <div className={styles.honeypot}>
            <label htmlFor="website">
              <input
                type="text"
                name="honeypot"
                id="website"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <span>Leave this field empty</span>
            </label>
          </div>
        </div>
        <div className={styles.submitContainer}>
          <Button
            className={styles.submitButton}
            type="submit"
            secondary
            icon="caretRight"
            label={loading ? i18n('pleaseWait') : i18n('submit')}
            disabled={loading}
          />
        </div>
      </form>
      <div
        className={styles.FormResults}
        id="order_inquiry_form_results"
      >
        {validationErrors.length > 0 && formSubmit && (
          <ContactFormResults
            status="error"
            errorMessage={<p>{i18n('pleaseFillTheRequiredFields')}</p>}
          />
        )}
        {formSubmit && !loading && validationErrors.length === 0 && (
          <>
            {formSubmitSuccess ? (
              <ContactFormResults
                data-gtm-visibility="success-message"
                status="success"
                successMessage={<p>{i18n('orderInquirySuccessMessage')}</p>}
              />
            ) : (
              <ContactFormResults
                status="error"
                errorMessage={<p>{i18n('somethingWentWrong')}</p>}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default OrderInquiryForm
